import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';

import EarningsPage from '../../components/OwnerReporting/earnings/EarningsPage';
import {
  parseEarningsQuery, applyPatch, buildPagePath, buildEarningsVariables, buildStatementUrl,
} from '../../components/OwnerReporting/shared/reportingUrlState';
import history from '../../core/history';

import getOwnerEarnings from './ownerEarnings.graphql';

export const EARNINGS_PATH = '/owner/earnings';

// Reads the filters from the URL (query given by the route), asks the server for the data and hands them to the page.
// A filter change = navigation to the new URL (the URL is the single source of truth: shareable, reloadable).
const OwnerEarningsContainer = ({ query }) => {
  const state = useMemo(() => parseEarningsQuery(query), [JSON.stringify(query || {})]);
  const convertCurrency = useSelector((store) => store.currency && store.currency.to) || undefined;
  const variables = useMemo(() => buildEarningsVariables(state, convertCurrency), [state, convertCurrency]);

  const { data, previousData, loading, error, refetch } = useQuery(getOwnerEarnings, {
    variables,
    fetchPolicy: 'network-only',
    ssr: false,
    notifyOnNetworkStatusChange: true,
  });

  // Keep the previous result visible (dimmed) while a new filter loads, instead of flashing the skeleton.
  const result = (data && data.getOwnerEarnings) || (loading && previousData ? previousData.getOwnerEarnings : null);

  // replace: true = no new history entry (used to leave an out-of-range page number)
  const go = (next, { replace = false } = {}) => history[replace ? 'replace' : 'push'](buildPagePath(EARNINGS_PATH, next));

  return (
    <EarningsPage
      state={state}
      loading={loading}
      networkError={Boolean(error)}
      earnings={result}
      statementUrl={buildStatementUrl(state, convertCurrency)}
      onChange={(patch, options) => go(applyPatch(state, patch), options)}
      onRetry={() => refetch()}
      onReset={() => go(applyPatch(state, { tab: 'all', search: '', listId: null }))}
    />
  );
};

OwnerEarningsContainer.propTypes = { query: PropTypes.object };

export default OwnerEarningsContainer;
